import { motion, useInView } from "framer-motion";
import { useRef } from "react";

// Section-level scroll reveal. Fades + rises + reveals children in sequence when
// a section enters the viewport. Use as a wrapper for any block that should
// animate in on scroll.
export function Reveal({ children, delay = 0, y = 30, className = "", as = "div", once = true, ...rest }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-80px 0px -60px 0px" });
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  );
}

// Masked line-by-line reveal for headings and taglines.
// Split any string into words separated by spaces; each word rises from a mask.
export function LineReveal({ text, className = "", stagger = 0.06, delay = 0 }) {
  const words = text.split(" ");
  return (
    <span className={`inline-block ${className}`} aria-label={text}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: delay + i * stagger, ease: [0.22, 1, 0.36, 1] }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

// Staggered fade-in for children list items.
export function StaggerChildren({ children, stagger = 0.08, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px 0px" });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={{
        hidden: {},
        show: { transition: { staggerChildren: stagger, delayChildren: 0.1 } },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const staggerItem = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};
